import { useContext, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, GlobalStyles, ThemeProvider } from '@mui/material';
import { AppContext } from '../../context/AppContext/context';
import {
  BookingSiteThemeContext,
  useResolvedBookingSiteTheme,
} from '../../context/BookingSiteTheme';
import { IS_PREVIEW } from '../../context/BookingSiteTheme/preview';
import { createThemeFromTokens } from '../../constants/theme';
import { themedSurface } from '../../constants/surface';
import ConfirmationModal from './ConfirmationModal';
import LoadingModal from './LoadingModal';
import PageLoading from './PageLoading';

function Layout() {
  const { state } = useContext(AppContext);
  const { confirmationModal, loadingModal } = state;

  const isConfirmationModalOpen = confirmationModal.status === 'awaiting';
  const isLoadingModalOpen = loadingModal.status === 'loading';

  const bookingSiteTheme = useResolvedBookingSiteTheme();
  const muiTheme = useMemo(
    () =>
      bookingSiteTheme ? createThemeFromTokens(bookingSiteTheme.tokens) : null,
    [bookingSiteTheme],
  );

  const modals = (
    <>
      <ConfirmationModal
        open={isConfirmationModalOpen}
        setOpen={() => confirmationModal.resolve?.(false)}
        negativeAction={() => confirmationModal.resolve?.(false)}
        positiveAction={() => confirmationModal.resolve?.(true)}
        message={confirmationModal.message}
        positiveText={confirmationModal.positiveText || 'Continue'}
        negativeText={confirmationModal.negativeText}
        title={confirmationModal.title}
      />

      <LoadingModal
        header={loadingModal.header}
        message={loadingModal.message}
        open={isLoadingModalOpen}
      />
    </>
  );

  // On body, not a wrapper: an untouched shop has no wrapper, and dialogs
  // portal out of this tree.
  const inertInPreview = IS_PREVIEW ? (
    <GlobalStyles
      styles={{ body: { pointerEvents: 'none', userSelect: 'none' } }}
    />
  ) : null;

  // Not a booking site page, or a theme we could not load — nothing to
  // theme, but the customizer still frames these.
  if (bookingSiteTheme === null) {
    return (
      <>
        {inertInPreview}
        <Outlet />
        {modals}
      </>
    );
  }

  // Hold the page until the theme is known. Rendering light-themed surfaces
  // over an already-painted dark background looks broken, not merely unstyled.
  if (!bookingSiteTheme || !muiTheme) {
    return <PageLoading />;
  }

  return (
    <ThemeProvider theme={muiTheme}>
      {inertInPreview}
      {/* customer-web renders no CssBaseline, so the canvas is browser white
          until we paint it — visible on overscroll and below the fold. */}
      {/* Exposed as CSS variables so the existing stylesheets and inline
          styles can opt in with a surfaceBg fallback, rather than
          every white panel needing the context. */}
      <GlobalStyles
        styles={{
          // A gradient paints across its own box, so every element carrying it
          // restarts the ramp. One fixed layer behind everything paints it
          // exactly once, viewport-sized, and never restarts however tall the
          // page or how much padding sits under the content.
          'body::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            background: bookingSiteTheme.tokens.bg,
            zIndex: -1,
          },
          // Only the root carries the fallback. A background on body would
          // paint over body::before — negative z-index children sit above the
          // root's background but below block descendants' backgrounds.
          //
          // Doubled selector on purpose: the defaults are declared on :root
          // (0-1-0), which outranks a bare `html` (0-0-1) whatever the source
          // order. :root:root is 0-2-0, so the themed values win.
          ':root:root': {
            background: bookingSiteTheme.tokens.base,
            // Portaled content — modals, popovers, tooltips — mounts on body,
            // outside the themed Box below, so it inherits from here or not at
            // all. Without this a native button falls back to `buttontext`.
            color: bookingSiteTheme.tokens.fg,
            ...themedSurface(bookingSiteTheme.tokens),
          },
          body: { background: 'transparent' },
        }}
      />
      <BookingSiteThemeContext.Provider value={bookingSiteTheme}>
        <Box
          sx={{
            minHeight: '100dvh',
            // Transparent — the fixed layer above is the only thing painting
            // the background. A second copy here is what restarted gradients.
            background: 'transparent',
            color: bookingSiteTheme.tokens.fg,
          }}>
          <Outlet />
        </Box>
        {modals}
      </BookingSiteThemeContext.Provider>
    </ThemeProvider>
  );
}

export default Layout;
